// Browser telemetry (docs/07-telemetry-posthog.md). Fails safe: the page must work when PostHog
// is unreachable or blocked. Only numbers, booleans and ids are sent — never text.
import posthog from "posthog-js";

import type { EventName } from "./events";

export type Props = Record<string, number | boolean | string | null>;

const FORBIDDEN = new Set([
  "name",
  "email",
  "github_login",
  "login",
  "answer",
  "text",
  "content",
  "pasted_text",
  "cv_text",
  "hiring_word",
  "word",
  "url",
  "link",
]);

export interface Telemetry {
  capture(event: EventName, props?: Props): void;
  /** False once a request failed or if PostHog never initialised. */
  healthy(): boolean;
}

export const noopTelemetry: Telemetry = { capture: () => undefined, healthy: () => false };

export function scrub(props: Props): Props {
  return Object.fromEntries(Object.entries(props).filter(([key]) => !FORBIDDEN.has(key)));
}

export function initTelemetry(apiKey: string, proxyPath: string, distinctId: string): Telemetry {
  if (!apiKey) return noopTelemetry;
  let ok = true;
  try {
    posthog.init(apiKey, {
      api_host: `${window.location.origin}${proxyPath}`,
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      disable_session_recording: true,
      disable_surveys: true,
      disable_web_experiments: true,
      disable_external_dependency_loading: true,
      advanced_disable_flags: true,
      persistence: "memory",
      disable_compression: true, // readable payloads: lets tests prove no answer text is sent
      request_batching: false, // events are few; send each one immediately
      // Keep events from automated browsers (navigator.webdriver, headless): an agent driving the
      // page is exactly what reviewers need to see. PostHog tags them $browser_type: "bot".
      opt_out_useragent_filter: true,
      ip: false,
      save_referrer: false,
      bootstrap: { distinctID: distinctId },
      on_request_error: () => {
        ok = false;
      },
    });
  } catch {
    return noopTelemetry;
  }
  return {
    capture(event, props = {}) {
      try {
        posthog.capture(event, scrub(props));
      } catch {
        ok = false;
      }
    },
    healthy: () => ok,
  };
}
